import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SEOService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly doc = inject(DOCUMENT);
    private readonly meta = inject(Meta);
    private readonly router = inject(Router);

    /**
     * Canonical link tag
     */
    private canonicalLink: HTMLLinkElement = null;

    /**
     * Hreflang link tags
     */
    private hreflangLinks: HTMLLinkElement[] = [];

    public constructor() {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            // reset meta description to empty value
            // (real content should be set by Features in ngOnInit of route Component)
            this.setMetaTag('description', '');
            // reset canonical tag
            this.resetCanonicalTag();
            // reset hreflang tags
            this.removeHreflangTags();
        });
    }

    public setMetaTag(name: string, content: string): void {
        this.meta.updateTag({ name, content });
    }

    public removeMetaTag(name: string): void {
        this.meta.removeTag(`name="${name}"`);
    }

    /**
     * @deprecated Use the new {@link setMetaTag} method instead.
     * Sets meta description tag
     * @param {string} content
     */
    public setMetaDescription(content: string): void {
        this.setMetaTag('description', content);
    }

    /**
     * Sets canonical link tag (`<link rel=canonical>`) with provided route
     * This method must be used with route alias defined in ecm
     * example : on `/compte/rdv-boutique` route, use canonical route `rdv-boutique` (without Feature root path)
     *           so crawlers will only index `rdv-boutique` path (and not `/compte/rdv-boutique` path)
     * @param {string} route Canonical Route
     */
    public setCanonicalRoute(route: string): void {
        if (isPlatformBrowser(this.platformId)) {
            if (!this.canonicalLink) {
                this.canonicalLink = this.doc.createElement('link');
                this.canonicalLink.setAttribute('rel', 'canonical');
                this.doc.head.appendChild(this.canonicalLink);
            }
            const origin: string = window.location.origin;
            const cleanRoute: string = route.replace(/^\/|\/$/g, ''); // escape leading/trailing slashes in route
            const canonicalUrl: string = origin + '/' + cleanRoute;
            this.canonicalLink.setAttribute('href', canonicalUrl);
        }
    }

    /**
     * Sets canonical link tag (`<link rel=canonical>`) with a full absolute URL.
     * Unlike {@link setCanonicalRoute}, this method accepts a complete URL
     * (e.g. `https://espace-client.orange.fr/page-accueil`).
     * @param {string} url Absolute canonical URL
     */
    public setCanonicalUrl(url: string): void {
        if (!this.canonicalLink) {
            this.canonicalLink = this.doc.createElement('link');
            this.canonicalLink.setAttribute('rel', 'canonical');
            this.doc.head.appendChild(this.canonicalLink);
        }
        this.canonicalLink.setAttribute('href', url);
    }

    /**
     * Adds an hreflang alternate link tag (`<link rel="alternate" hreflang="..." href="...">`)
     * Used for international/regional SEO targeting (e.g. fr-FR / fr-RE).
     * @param {string} hreflang Language-region code (e.g. 'fr-FR', 'fr-RE', 'x-default')
     * @param {string} href Absolute URL of the alternate page
     */
    public setHreflangTag(hreflang: string, href: string): void {
        const link: HTMLLinkElement = this.doc.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', hreflang);
        link.setAttribute('href', href);
        this.doc.head.appendChild(link);
        this.hreflangLinks.push(link);
    }

    /**
     * Removes all hreflang alternate link tags previously added
     */
    public removeHreflangTags(): void {
        this.hreflangLinks.forEach((link) => link.remove());
        this.hreflangLinks = [];
    }

    /**
     * Resets canonical link tag by removing `href` attribute
     */
    private resetCanonicalTag(): void {
        if (this.canonicalLink) {
            this.canonicalLink.removeAttribute('href');
        }
    }
}
